"use client";

import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import * as React from "react";
import type { Color } from "../../lib/colors";
import { cva, type VariantProps } from "../../lib/cva";
import { sortableStyle, DND_DRAGGABLE_PROPS, DND_TOUCH_ACTIVATION } from "../../lib/dnd-attrs";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import { Box } from "./box";
import {
  ContextMenu,
  ContextMenuPopup,
  ContextMenuTrigger,
} from "./context-menu";
import { Input } from "./input";
import { TAB_PILL_SURFACE_CLASS } from "./tab-pill-surface";
import { Text } from "./text";

export const tabsListVariants = cva(
  [
    "relative z-0 flex w-fit items-center justify-center text-muted-foreground",
    "data-[orientation=vertical]:flex-col",
  ],
  {
    compoundVariants: [
      // The segmented track's radius is a size metric: concentric with the
      // thumb across the 2px inset, so each rung carries its own pair.
      { class: "rounded-lg", size: "default", variant: "default" },
      { class: "rounded-md", size: "sm", variant: "default" },
    ],
    defaultVariants: { fullWidth: false, size: "default", variant: "default" },
    variants: {
      fullWidth: { false: "", true: "w-full" },
      // The metric rung of the segmented (`default`) genre only — the other
      // genres own their heights. `sm` is the 24px toolbar/header-line rung:
      // 20px segments in a 2px track, 12px label.
      size: { default: "", sm: "" },
      variant: {
        // The track is a recessed WELL, not a resting panel: `muted` sits only
      // ~2 L off the page and reads as part of it, so the track paints
      // `sunken` — below the page in dark, a clear step under it in light —
      // and the thumb pops off it without any outline around the track.
      default: "gap-x-0.5 bg-sunken p-0.5 text-muted-foreground",
        // Independent page-view pills. The list paints no shared track, so
        // every tab keeps a quiet surface of its own.
        pill: "gap-x-1 overflow-visible",
        // The strip's height is the tab's own 40px; the hover wash is the
        // tab's centered 28px pill, so the list adds no padding of its
        // own. The list keeps a small gap between adjacent hover pills. Each
        // tab owns its 8px horizontal inset inside that wash.
        underline: "gap-x-1 data-[orientation=vertical]:px-1",
      },
    },
  },
);

export type TabsVariant = NonNullable<
  VariantProps<typeof tabsListVariants>["variant"]
>;

export type TabsSize = NonNullable<
  VariantProps<typeof tabsListVariants>["size"]
>;

// The three variants are different genres with different metrics. A tab must
// style to its list's genre (its size rung, and whether the strip stretches)
// without every call site restating them.
const TabsVariantContext = React.createContext<{
  fullWidth: boolean;
  size: TabsSize;
  variant: TabsVariant;
}>({ fullWidth: false, size: "default", variant: "default" });

/** Points a consumer's `ref` — callback or object — at the node. */
function assignRef<T>(ref: React.Ref<T> | undefined, node: T | null): void {
  if (typeof ref === "function") {
    ref(node);
  } else if (ref) {
    ref.current = node;
  }
}

export function Tabs({
  className,
  ...props
}: TabsPrimitive.Root.Props): React.ReactElement {
  return (
    <TabsPrimitive.Root
      // `min-h-0` is not decoration. The root is nearly always a flex item, and
      // a flex item's `min-height` defaults to `auto` — its content-based
      // minimum — so without this the root refuses to shrink to its parent and
      // instead inflates to the full height of the tallest panel. The panel
      // below then gets a content-height box, its `overflow` has nothing to
      // scroll, and the overflow is silently clipped by whatever ancestor
      // hides it. Every call site had to hand-patch this.
      className={cn(
        "flex min-h-0 flex-col gap-2 data-[orientation=vertical]:flex-row",
        className,
      )}
      data-slot="tabs"
      {...props}
    />
  );
}

export const tabsIndicatorVariants = cva(
  "absolute bottom-0 left-0 h-(--active-tab-height) w-(--active-tab-width) translate-x-(--active-tab-left) -translate-y-(--active-tab-bottom) transition-[width,translate] duration-base ease-in-out",
  {
    compoundVariants: [
      // Neutral by default — the bar is the active label's own ink —
      // with the color name routed through `data-color` when themed.
      { class: "bg-foreground", color: false, variant: "underline" },
      { class: "bg-c", color: true, variant: "underline" },
      // Thumb radius pairs with the track's per-rung radius (see
      // `tabsListVariants`).
      { class: "rounded-md", size: "default", variant: "default" },
      { class: "rounded-sm", size: "sm", variant: "default" },
    ],
    defaultVariants: { color: false, size: "default", variant: "default" },
    variants: {
      // Boolean themed/neutral axis. Only the underline bar paints it, so the
      // classes live in `compoundVariants`.
      color: { false: "", true: "" },
      size: { default: "", sm: "" },
      variant: {
        // The thumb has to sit ABOVE the `bg-muted` track, and which surface
        // does that flips with the mode: in light the page is over muted, in
        // dark it is under it. Hence the branch. A label sits on this thumb,
        // so it needs a SURFACE, never a hairline token as FILL: a palette
        // whose hairline is bright (a white brutalist edge) painted
        // white-on-white here. Dark uses `accent` — the neutral selected
        // surface — because `card` sits only ~1.5 L above the dark track, too
        // close for the selected segment to read. The fill steps alone are
        // deliberately quiet, so the thumb's EDGE does the separating: a
        // hairline of matched strength per mode (light `border` on the white
        // thumb and dark `border-strong` on the accent thumb are both a
        // ~5 L step off their fill).
        default:
          "-z-1 border border-border bg-background shadow-sm/5 dark:border-border-strong dark:bg-accent",
        // A pill paints its own selected surface. There is no moving bar in
        // this genre.
        pill: "hidden",
        underline:
          "z-10 data-[orientation=horizontal]:h-0.5 data-[orientation=vertical]:w-0.5 data-[orientation=vertical]:-translate-x-px data-[orientation=horizontal]:translate-y-px",
      },
    },
  },
);

export type TabsListProps = TabsPrimitive.List.Props &
  Omittable<{
    variant?: TabsVariant;
    /** Metric rung for the segmented (`default`) genre: `sm` is the 24px
     *  toolbar/header-line control (20px segments, 12px label). The other
     *  genres carry their own metrics and ignore this prop. */
    size?: TabsSize;
    /** Stretch the list across its container. Tabs already grow equally. In
     *  the `underline` genre a stretched strip's tabs are blocks dividing the
     *  width, so their hover wash fills the whole tab instead of the hugging
     *  strip's centered 28px pill. */
    fullWidth?: VariantProps<typeof tabsListVariants>["fullWidth"];
    /** Active-bar color for the `underline` variant. Default is the neutral
     *  foreground ink (white in dark, near-black in light); pass a color name
     *  (e.g. `"primary"`) for a themed bar. Other variants ignore this prop. */
    color?: Color;
    /** The values of the tabs that drag to reorder, in display order. Set, it
     *  makes the row a drag context, and MEMBERSHIP is what makes a tab
     *  draggable: a `TabsTab` whose value is in this array is a drag source
     *  and a drop target; a tab left out is pinned — it holds its seat, and
     *  the drag clamp does not let a sortable tab travel over it. Render the
     *  tabs in this same order. Unset, the row is a plain strip.
     *
     *  An array of arrays is several RUNS: each inner array reorders among
     *  itself and a drag never crosses from one run into the next — the
     *  clamp stops at the run's own edges and the drop targets are the run's
     *  own tabs. A strip that leads with a pinned run and follows with the
     *  rest is two runs; the host draws the boundary between them. */
    order?: string[] | string[][];
    /** Fires with the full reordered sequence after a drop — flat, run after
     *  run, when `order` is runs. */
    onOrderChange?: (order: string[]) => void;
  }>;

export function TabsList(props: TabsListProps): React.ReactElement {
  const { order, onOrderChange, ...rest } = props;
  // Components branch where hooks cannot: only a row with an `order` mounts
  // the drag context, so a static strip pays nothing for the axis.
  if (order === undefined) return <TabsListBase {...rest} />;
  return (
    <SortableTabsList onOrderChange={onOrderChange} order={order} {...rest} />
  );
}

/** The styled strip: genre, size rung, and the moving indicator. */
function TabsListBase({
  variant = "default",
  size = "default",
  color,
  fullWidth,
  className,
  children,
  ...props
}: Omit<TabsListProps, "order" | "onOrderChange">): React.ReactElement {
  const context = React.useMemo(
    () => ({ fullWidth: Boolean(fullWidth), size, variant }),
    [fullWidth, size, variant],
  );
  return (
    <TabsPrimitive.List
      className={cn(tabsListVariants({ fullWidth, size, variant }), className)}
      data-slot="tabs-list"
      {...props}
    >
      <TabsVariantContext.Provider value={context}>
        {children}
      </TabsVariantContext.Provider>
      <TabsPrimitive.Indicator
        data-color={variant === "underline" ? color : undefined}
        className={tabsIndicatorVariants({
          color: Boolean(color),
          size,
          variant,
        })}
        data-slot="tab-indicator"
      />
    </TabsPrimitive.List>
  );
}

type TabsSortContextValue = {
  /** Set while a gesture crosses the drag threshold, so the click that
   *  follows a drop does not also select the tab it landed on. Cleared on the
   *  next pointer-down — clearing it when `isDragging` flips back to false
   *  would race the click event that fires right after the drop. */
  draggedRef: React.RefObject<boolean>;
  /** Each drag source's run index — the tabs in the list's `order`, and
   *  which of its runs each one belongs to. */
  runOf: ReadonlyMap<string, number>;
};

const TabsSortContext = React.createContext<TabsSortContextValue | null>(null);

type SortableTabsListProps = Omit<TabsListProps, "order" | "onOrderChange"> & {
  order: string[] | string[][];
  onOrderChange: ((order: string[]) => void) | undefined;
};

/** The strip as a drag context: `DndContext` + `SortableContext` around the
 *  styled list, with the reorder clamped to the sortable tabs' own slots. */
function SortableTabsList({
  order,
  onOrderChange,
  ref,
  children,
  ...props
}: SortableTabsListProps): React.ReactElement {
  const draggedRef = React.useRef(false);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const horizontalBoundsRef = React.useRef<{
    maximumX: number;
    minimumX: number;
  } | null>(null);
  // DndContext otherwise allocates a module-global numeric ID. That counter
  // can differ between server render and hydration when a page mounts several
  // sortable rows. React's ID stays stable on both sides.
  const contextId = React.useId();

  const setListRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      assignRef(ref, node);
    },
    [ref],
  );

  // A distance constraint keeps a plain click a click: only a gesture that
  // travels past the threshold becomes a drag. A finger drags after a hold
  // instead, so a swipe along an overflowing strip still scrolls it.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );

  const restrictToTabSlots = React.useCallback<Modifier>(
    ({ activeNodeRect, transform }) => {
      const bounds = horizontalBoundsRef.current;
      if (!activeNodeRect || !bounds) return { ...transform, y: 0 };
      return {
        ...transform,
        x: Math.min(
          bounds.maximumX - activeNodeRect.right,
          Math.max(bounds.minimumX - activeNodeRect.left, transform.x),
        ),
        y: 0,
      };
    },
    [],
  );

  // One run or several: every tab drags among the tabs of its own run. The
  // flat sequence is what the sortable context sorts and what a drop reports;
  // the run index is what the clamp, the drop targets and the tab's marker
  // read.
  const runs = React.useMemo<string[][]>(
    () =>
      order.length > 0 && Array.isArray(order[0])
        ? (order as string[][])
        : [order as string[]],
    [order],
  );
  const flat = React.useMemo(() => runs.flat(), [runs]);
  const runOf = React.useMemo(() => {
    const map = new Map<string, number>();
    runs.forEach((run, index) => {
      for (const value of run) map.set(value, index);
    });
    return map;
  }, [runs]);

  const captureHorizontalBounds = React.useCallback(
    ({ active }: DragStartEvent) => {
      // Overflow-hidden tabs are out of the flow, so their boxes are not real
      // slots — clamping to them would let a drag travel past the visible
      // row. Only the active tab's OWN RUN bounds the drag: a pinned tab in
      // the row is not a slot, and a tab in another run is a slot this drag
      // can never take — clamping to either would let a drag hover over a
      // tab it cannot displace.
      const tabs = listRef.current?.querySelectorAll<HTMLElement>(
        `[data-slot="tabs-tab"][data-tab-run="${runOf.get(String(active.id))}"]:not([data-tab-overflow])`,
      );
      // A run with no tabs has no edges to clamp to. One tab is enough: it
      // is its own first and last.
      if (!tabs?.length) {
        horizontalBoundsRef.current = null;
        return;
      }
      horizontalBoundsRef.current = {
        minimumX: tabs.item(0).getBoundingClientRect().left,
        maximumX: tabs.item(tabs.length - 1).getBoundingClientRect().right,
      };
    },
    [runOf],
  );

  // Drop targets are the active tab's own run. `closestCenter` over every
  // sortable would hand a tab clamped at its run's edge to the neighbouring
  // run's nearest tab, and the drop would cross the boundary the clamp held.
  const collisionDetection = React.useCallback<CollisionDetection>(
    (args) => {
      const run = runOf.get(String(args.active.id));
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (container) => runOf.get(String(container.id)) === run,
        ),
      });
    },
    [runOf],
  );

  const handleDragEnd = React.useCallback(
    ({ active, over }: DragEndEvent) => {
      horizontalBoundsRef.current = null;
      if (!over || active.id === over.id) return;
      // Runs are contiguous in the flat sequence, so a move inside one keeps
      // every run whole.
      if (runOf.get(String(active.id)) !== runOf.get(String(over.id))) return;
      const from = flat.indexOf(String(active.id));
      const to = flat.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      onOrderChange?.(arrayMove(flat, from, to));
    },
    [flat, runOf, onOrderChange],
  );

  const sort = React.useMemo(() => ({ draggedRef, runOf }), [runOf]);

  return (
    <DndContext
      id={contextId}
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={[restrictToTabSlots]}
      onDragStart={captureHorizontalBounds}
      onDragCancel={() => {
        horizontalBoundsRef.current = null;
      }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={flat} strategy={horizontalListSortingStrategy}>
        <TabsSortContext.Provider value={sort}>
          <TabsListBase ref={setListRef} {...props}>
            {children}
          </TabsListBase>
        </TabsSortContext.Provider>
      </SortableContext>
    </DndContext>
  );
}

export const tabsTabVariants = cva(
  "relative flex shrink-0 grow cursor-pointer select-none items-center justify-center whitespace-nowrap rounded-md border border-transparent outline-none focus-visible:ring-2 focus-visible:ring-ring data-disabled:pointer-events-none data-[orientation=vertical]:w-full data-[orientation=vertical]:justify-start data-active:text-foreground data-disabled:opacity-64 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    compoundVariants: [
      // Segmented-genre metrics per rung. The default rung keeps its
      // viewport-responsive step (36px → 32px at the `sm` breakpoint); the
      // `sm` RUNG is one fixed 20px segment on the 12px-label line, its
      // radius one step inside the thumb's.
      {
        class:
          "h-8 gap-1.5 px-[calc(--spacing(2.5)-1px)] text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:-mx-0.5",
        size: "default",
        variant: "default",
      },
      {
        class:
          "h-5 gap-1.5 rounded-sm px-[calc(--spacing(2.5)-1px)] text-xs [&_svg:not([class*='size-'])]:size-3.5",
        size: "sm",
        variant: "default",
      },
      // The underline hover wash's shape follows whether the strip stretches.
      // A hugging strip sizes each tab to its content, and its wash is a
      // centered 28px pill: the 4px leading inset seats the 16px view-tile
      // slot 4px in from the wash's edge, 6px above and below, and `-mx-px`
      // overlaps the transparent borders outside the wash.
      {
        class:
          "before:inset-x-0 before:top-1/2 before:h-7 before:-translate-y-1/2 before:rounded-md",
        fullWidth: false,
        variant: "underline",
      },
      // A stretched strip's tabs are blocks dividing the width, so the wash
      // is square and fills the whole tab — 1px past the padding box to paint
      // over the base's transparent border, which `-mx-px` folds into the
      // list gap, so adjacent washes keep the list's daylight.
      {
        class: "before:-inset-px",
        fullWidth: true,
        variant: "underline",
      },
      // A trailing glyph seats like the leading tile. The 8px trailing inset
      // is a TEXT metric — a label's last letter is ink to its edge — and a
      // glyph box after it carries its own internal whitespace, so behind a
      // glyph that inset reads as twice the air the tile gets. It closes to
      // the tile's 4px.
      {
        class: "pe-1",
        trailing: true,
        variant: "underline",
      },
    ],
    defaultVariants: {
      fullWidth: false,
      size: "default",
      trailing: false,
      variant: "default",
    },
    variants: {
      // Whether the list stretches (`TabsList fullWidth`). Only the underline
      // wash reads it, so the classes live in `compoundVariants`.
      fullWidth: { false: "", true: "" },
      // Whether the tab seats a `trailing` glyph after its label. Only the
      // underline inset reads it, so the class lives in `compoundVariants`.
      trailing: { false: "", true: "" },
      size: { default: "", sm: "" },
      variant: {
        default:
          "font-medium transition-[color,background-color,box-shadow] not-data-active:hover:text-foreground",
        // The 28px desktop control rung (32px below `sm` for touch), matching
        // Button `size="sm"` so a tab and the rail control beside it hold one
        // line — including the rung's 9px inset, which keeps the side air in
        // proportion to the 28px height's vertical air.
        pill:
          `h-7 gap-1.5 rounded-full px-[calc(--spacing(2.5)-1px)] font-medium text-sm [&_svg:not([class*='size-'])]:size-4 ${TAB_PILL_SURFACE_CLASS}`,
        // The view-tab strip rung: a 40px item whose hover state is a wash
        // (the `before` layer, under the label but over the list, like the
        // indicator; its shape is the `fullWidth` compound above), 12px/600
        // label, 16px glyphs, and a true 6px glyph-to-label gap (no svg
        // margin pull-in) — the same rung the other tab variants hold. The
        // 4px leading and 8px trailing insets are real padding inside the
        // wash (the trailing one closes to 4px behind a `trailing` glyph —
        // the compound above). Content stays centered (the base): a tab
        // that grows in a `fullWidth` strip keeps its label under the middle
        // of its own bar, and a hugging strip sizes each tab to its content,
        // where alignment is moot. The focus ring wears the wash's shape too
        // — it moves off the 40px tab box onto the `before` layer, so keyboard
        // focus outlines the same pill the pointer hovers rather than the
        // strip-height cell.
        underline:
          "-mx-px h-10 gap-1.5 ps-1 pe-2 font-semibold text-xs transition-[color,box-shadow] not-data-active:hover:text-muted-foreground before:absolute before:-z-1 hover:before:bg-wash-hover focus-visible:ring-0 focus-visible:before:ring-2 focus-visible:before:ring-ring [&_svg:not([class*='size-'])]:size-4",
      },
    },
  },
);

// `contextMenu` is also a (long-dead) HTML attribute React still types as a
// string on every element; the tab's own menu prop replaces it.
export type TabsTabProps = Omit<TabsPrimitive.Tab.Props, "contextMenu"> &
  Omittable<{
    /** Items for the tab's right-click menu. Rendered in a popup anchored to
     *  the TAB — not to the cursor, which is what a bare context menu does —
     *  so the menu reads as belonging to the tab it acts on. Omitted, the tab
     *  has no menu and right-click falls through to the browser's own.
     *
     *  A function receives a `close` callback, for content that is not a menu
     *  item — a footer action button — and therefore does not dismiss the
     *  menu on its own. */
    contextMenu?: React.ReactNode | ((close: () => void) => React.ReactNode);
    /** The tab is hosting a `TabsTabInput` in place of its label. Dragging is
     *  suspended — a selection drag across the field would otherwise pick the
     *  tab up — and the menu leaves focus alone when it closes, so the field
     *  the menu opened keeps the caret instead of losing it back to the tab. */
    editing?: boolean;
    /** The tab lost its slot to the strip's overflow (see `TabsOverflow`): it
     *  stays mounted so the strip keeps a real width to measure, but it
     *  leaves the flow, paints nothing, and cannot be focused, activated, or
     *  dragged. The host offers it through its overflow menu instead. */
    overflowHidden?: boolean;
    /** One glyph seated after the label, in the label's own ink — a state
     *  the tab carries (pinned, unsaved), never a second identity. Pass an
     *  `Icon` at `size="xs"`. The underline strip closes its trailing inset
     *  to the 4px that seats the leading tile, so the glyph sits inside the
     *  wash the way the tile does. Not rendered while `editing`: the field
     *  takes the label's slot, and the mark goes with the label it follows. */
    trailing?: React.ReactNode;
  }>;

/** A tab of its list's genre. In a list with an `order`, a tab whose value is
 *  in that array is also the drag source: the whole tab drags — there is no
 *  separate grip, because the tab has no gutter to host one. */
export function TabsTab(props: TabsTabProps): React.ReactElement {
  const sort = React.useContext(TabsSortContext);
  const value = props.value as unknown;
  // Components branch where hooks cannot: only a tab in the list's `order`
  // mounts the sortable's hooks; a pinned tab and a static strip's tabs stay
  // plain elements.
  if (sort !== null && typeof value === "string" && sort.runOf.has(value)) {
    return <SortableTabsTab {...props} sort={sort} value={value} />;
  }
  return <TabsTabFrame {...props} />;
}

/** The styled tab element with its optional menu, its inline-edit host, and
 *  its overflow-hidden state — everything a tab is besides a drag source. */
function TabsTabFrame({
  className,
  children,
  contextMenu,
  editing = false,
  overflowHidden = false,
  trailing,
  ref,
  ...props
}: TabsTabProps): React.ReactElement {
  const { fullWidth, size, variant } = React.useContext(TabsVariantContext);

  // The mark trails the LABEL, so while a field stands in for the label there
  // is nothing for it to follow — and the inset it closed reopens with it.
  const mark = editing ? null : trailing;
  const hasMark = mark !== null && mark !== undefined && mark !== false;

  // Controlled so the menu's own content can dismiss it: a footer button is
  // not a menu item, and only items close on click.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const closeMenu = React.useCallback(() => setMenuOpen(false), []);

  // The menu anchors to the tab element, so the node has to be readable here
  // as well as by whoever else asked for it.
  const nodeRef = React.useRef<HTMLElement | null>(null);
  const setRef = React.useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      assignRef(ref, node);
    },
    [ref],
  );

  const tab = (
    <TabsPrimitive.Tab
      ref={setRef}
      className={cn(
        tabsTabVariants({ fullWidth, size, trailing: hasMark, variant }),
        // Out of the flow but still a real box: the overflow strip re-measures
        // every tab's width each pass, so a hidden tab must keep one. The
        // attribute is what the drag-bounds query excludes.
        overflowHidden && "invisible absolute",
        className,
      )}
      // Re-asserted over whatever wraps the element: as a context-menu
      // trigger this element is cloned with the trigger's own `data-slot`,
      // and the element's props are what win. Without it the row's
      // drag-bounds query and the overflow measure both stop matching the
      // tab.
      data-slot="tabs-tab"
      data-tabs-variant={variant}
      data-tab-overflow={overflowHidden ? "" : undefined}
      {...props}
      {...(overflowHidden ? { disabled: true } : {})}
      // A field is interactive content, and interactive content inside a
      // `<button>` is inert in Chromium: the caret never lands, so the editor
      // could not be typed in at all. While the tab hosts one it renders as a
      // plain element instead — same slot, same tab semantics from Base UI,
      // no button swallowing the field's gestures.
      {...(editing ? { render: <Box />, nativeButton: false } : {})}
    >
      {children}
      {mark}
    </TabsPrimitive.Tab>
  );

  if (!contextMenu) return tab;

  return (
    <ContextMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <ContextMenuTrigger render={tab} />
      {/* `anchor` overrides the cursor position a context menu would otherwise
          open at, and `align="start"` hangs the popup off the tab's leading
          edge. */}
      <ContextMenuPopup
        align="start"
        anchor={nodeRef}
        finalFocus={editing ? false : undefined}
        side="bottom"
        width="panel"
      >
        {typeof contextMenu === "function" ? contextMenu(closeMenu) : contextMenu}
      </ContextMenuPopup>
    </ContextMenu>
  );
}

type SortableTabsTabProps = TabsTabProps & {
  sort: TabsSortContextValue;
  value: string;
};

/** A `TabsTabFrame` that is also the drag source. */
function SortableTabsTab({
  sort: { draggedRef, runOf },
  value,
  editing = false,
  overflowHidden = false,
  ref,
  style,
  onClickCapture,
  onPointerDown,
  ...props
}: SortableTabsTabProps): React.ReactElement {
  const {
    // Strip `role` (the tab underneath is the real `tab`), `tabIndex` (Base
    // UI's roving focus owns the tab stops — a second one would add a stray
    // stop), and `aria-disabled` (it reports the DRAG SOURCE's state, and on
    // this element it announces — and to a test harness, behaves as — a
    // disabled tab, which is what suspending the drag for an inline edit would
    // otherwise make of every tab being renamed).
    attributes: {
      role: _role,
      tabIndex: _tabIndex,
      "aria-disabled": _ariaDisabled,
      ...attributes
    },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: value, disabled: editing || overflowHidden });

  const setRef = React.useCallback(
    (node: HTMLElement | null) => {
      setNodeRef(node);
      assignRef(ref, node);
    },
    [ref, setNodeRef],
  );

  React.useEffect(() => {
    if (isDragging) draggedRef.current = true;
  }, [isDragging, draggedRef]);

  return (
    <TabsTabFrame
      {...props}
      ref={setRef}
      value={value}
      editing={editing}
      overflowHidden={overflowHidden}
      // What the drag-bounds query selects on: the tab's run, so only its
      // own run's tabs bound its drag. A pinned tab sharing the row carries
      // no marker and bounds nothing.
      data-tab-run={runOf.get(value)}
      style={{
        ...style,
        ...sortableStyle(transform, {
          transition,
          isSorting,
          axis: "x",
          translateOnly: true,
        }),
        // Transformed flex items overlap while dragging; lift the active one so
        // it renders above the tabs it crosses. Promote only the active drag
        // source: displaced tabs already animate their transforms through the
        // compositor, while this tab follows pointer-driven transform updates.
        zIndex: isDragging ? 10 : undefined,
        cursor: isDragging ? "grabbing" : undefined,
        willChange: isDragging ? "transform" : undefined,
      }}
      onClickCapture={(event) => {
        if (draggedRef.current) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onClickCapture?.(event);
      }}
      {...attributes}
      {...listeners}
      // The drag markers turn text selection off. While the tab hosts a field,
      // selecting its text is the point.
      {...(editing ? {} : DND_DRAGGABLE_PROPS)}
      // Every press — mouse or finger — starts with the flag down, or one drag
      // would latch it and the tab could never be clicked again. The sensors'
      // own listeners are `onMouseDown`/`onTouchStart`, so this handler shares
      // no key with the spread above.
      onPointerDown={(event) => {
        draggedRef.current = false;
        onPointerDown?.(event);
      }}
    />
  );
}

export function TabsPanel({
  className,
  ...props
}: TabsPrimitive.Panel.Props): React.ReactElement {
  return (
    <TabsPrimitive.Panel
      // A panel is a flex column so its `flex-1` resolves against the tab
      // container and a filling child (ScrollArea, DataTable) gets a real
      // height instead of collapsing to content. `min-h-0` is the other half of
      // that `flex-1`: claiming the free space is useless if the automatic
      // minimum size then forbids shrinking below the content.
      className={cn("flex min-h-0 flex-1 flex-col outline-none", className)}
      data-slot="tabs-content"
      {...props}
    />
  );
}

export type TabsTabInputProps = {
  /** Label the field opens with. The field owns the draft from there — the
   *  host re-renders with the committed value once it lands. */
  defaultValue: string;
  /** Persist the trimmed draft. Never fires with an empty string, or with the
   *  value it opened on — both of those cancel instead. */
  onCommit: (next: string) => void;
  /** The edit ended without a change: Escape, an emptied field, or a blur that
   *  typed nothing new. */
  onCancel: () => void;
  maxLength?: number;
  "aria-label"?: string;
};

/**
 * The tab's label, swapped for a field, in place. Render it as a `TabsTab`
 * child where the label text would go, with the tab in `editing` mode.
 *
 * It inherits the tab's own font, and an invisible in-flow sizer mirrors the
 * draft so the field hugs the text it holds — the tab is the same width mid-
 * rename that it will be once the name lands, and the row never jumps. The 4px
 * caret gutter is pulled back out with a negative margin, so the glyphs do not
 * shift when the field appears; only the ring around them is new.
 *
 * A field living inside a control has to keep that control's gestures off its
 * own: a drag across the glyphs is a text selection, not a tab pick-up, and
 * Enter commits the name instead of activating the tab. So every pointer and
 * key event stops at the field.
 */
export function TabsTabInput({
  defaultValue,
  onCommit,
  onCancel,
  maxLength = 60,
  "aria-label": ariaLabel,
}: TabsTabInputProps): React.ReactElement {
  const [draft, setDraft] = React.useState(defaultValue);
  // Enter and Escape both end the edit and drop focus, and the blur that
  // follows would otherwise resolve it a second time.
  const settledRef = React.useRef(false);

  const settle = React.useCallback(
    (commit: boolean) => {
      if (settledRef.current) return;
      settledRef.current = true;
      const next = draft.trim();
      if (commit && next && next !== defaultValue) onCommit(next);
      else onCancel();
    },
    [draft, defaultValue, onCommit, onCancel],
  );

  const stop = React.useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <Box
      align="center"
      direction="row"
      maxWidth={224}
      minWidth={40}
      overflow="hidden"
      position="relative"
      rounded="md"
      // The ring paints INSET because this box's trailing edge lands exactly
      // on the overflow viewport's clip edge when the host tab is the strip's
      // last: anything drawn outside the box (an outside ring) is cut flat
      // there, leaving the field open-ended on the right.
      className="-mx-1 inset-ring-1 inset-ring-ring"
      onClick={stop}
      onPointerDown={stop}
      onKeyDown={(event: React.KeyboardEvent) => {
        // Typing must not reach the tab row: the arrow keys are its roving
        // focus, and Enter/Space activate the focused tab.
        event.stopPropagation();
        if (event.key === "Enter") settle(true);
        if (event.key === "Escape") settle(false);
      }}
    >
      {/* Width driver only — the same glyphs, unpainted, in the flow. */}
      <Text
        aria-hidden
        className="pointer-events-none whitespace-pre px-1 font-[inherit] text-[inherit] opacity-0"
      >
        {draft || " "}
      </Text>
      {/* `bare` and `[font:inherit]`: the field wears the tab's own type, and
          owns no metrics of its own, so the swap is invisible. */}
      <Input
        aria-label={ariaLabel}
        autoFocus
        bare
        className="absolute inset-0 h-full w-full min-w-0 bg-transparent px-1 text-foreground outline-none [font:inherit]"
        maxLength={maxLength}
        onBlur={() => settle(true)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setDraft(event.target.value)
        }
        onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
          event.currentTarget.select()
        }
        value={draft}
      />
    </Box>
  );
}
